
import {
  defineComponent,
  reactive,
  toRefs,
  onMounted,
  computed,
  Ref
} from "vue";
import EntitySelectorTransportador from "../../components/transportador/EntitySelectorTransportador.vue";
import { useAuth, PermissionPayload } from "../../modules/auth";
import { formatDate, DATE_SELECTORS } from "../../modules/utils";
import {
  useLicencaFaturamentoService,
  LicencaFaturamentoPayload,
  LicencaFaturamentoFilterPayload,
  LicencaFaturamentoFilterReturnPayload,
  TipoLicencaFaturamentoPayload
} from "../../modules/business/licenca/licencaFaturamentoBusiness";
import { ClientePayload } from "../../modules/business/cliente/clienteBusiness";
import EntitySelectorCliente from "../../components/cliente/EntitySelectorCliente.vue";
import { TransportadorPayload } from "../../modules/business/transportador/transportadorBusiness";
import { PaginationResult, defaultResults } from "../../pagination";
import { LicencaOrgaoPayload } from "../../modules/business/licenca/licencaOrgaoBusiness";
import { useToast } from "../../modules/message";

interface CombinacaoVeiculoState {
  filter: LicencaFaturamentoFilterPayload;
  cliente?: ClientePayload | null;
  stateLoading: boolean;
  stateReady: boolean;
  tiposDisponiveis: Array<TipoLicencaFaturamentoPayload>;
  tiposDisponiveisRoot: Array<TipoLicencaFaturamentoPayload>;
  periodoFixo: string;
  page: number;
  licencaOrgaoSelected: Array<number>;
  results: PaginationResult<LicencaOrgaoPayload>;
  loadings: Array<Ref<boolean>>;
  tipoPaginamento: string;
  descricaoBusca: string;
}

export default defineComponent({
  setup() {
    const state = reactive<CombinacaoVeiculoState>({
      filter: { statusFatura: "TOD" },
      cliente: null,
      stateLoading: false,
      stateReady: false,
      tiposDisponiveis: useLicencaFaturamentoService().getTiposLicencaFaturamento(),
      tiposDisponiveisRoot: [
        { mensagem: "Faturada", flag: "FAT" },
        { mensagem: "Bonificada", flag: "BON" },
        { mensagem: "Deletada", flag: "DEL" }
      ],
      periodoFixo: DATE_SELECTORS.MONTH,
      page: 0,
      licencaOrgaoSelected: [],
      results: defaultResults(),
      loadings: [],
      tipoPaginamento: "search" as "afaturar" | "faturadas",
      descricaoBusca: ""
    });

    const amIAllowed = (
      authority: string,
      permissionInfo?: PermissionPayload | string | null
    ) => {
      return useAuth().amIAllowed(authority, permissionInfo);
    };

    const stateBlocked = computed(() => {
      if (state.stateLoading) {
        return true;
      } else if (amIAllowed("view", "root-cliente")) {
        return !state.filter.idCliente;
      }
      return false;
    });

    const executeSearch = () => {
      state.stateLoading = true;
      const {
        data,
        loading,
        searchLicencaFaturamento
      } = useLicencaFaturamentoService();
      state.loadings.push(loading);
      return searchLicencaFaturamento(state.filter, state.page)
        .then(() => {
          if (data.value && data.value.content) {
            const resultTratado: LicencaFaturamentoPayload[] = data.value.content.map(
              (item: LicencaFaturamentoFilterReturnPayload, index: number) => {
                item.groupIndex = index;
                return item;
              }
            );
            data.value.content = resultTratado;
            state.results = data.value;
          } else {
            state.results = defaultResults();
          }
          state.stateLoading = false;
        })
        .catch(() => {
          state.stateLoading = false;
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    };

    const executeAetsToFaturar = () => {
      state.stateLoading = true;
      const {
        data,
        loading,
        searchAetsToFaturar
      } = useLicencaFaturamentoService();
      state.loadings.push(loading);
      return searchAetsToFaturar(state.page)
        .then(() => {
          if (data.value && data.value.content) {
            const resultTratado: LicencaFaturamentoPayload[] = data.value.content.map(
              (item: LicencaFaturamentoFilterReturnPayload, index: number) => {
                item.groupIndex = index;
                return item;
              }
            );
            data.value.content = resultTratado;
            state.results = data.value;
          } else {
            state.results = defaultResults();
          }
          state.stateLoading = false;
        })
        .catch(() => {
          state.stateLoading = false;
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    };

    const executeDownload = () => {
      state.stateLoading = true;
      const { downloadFaturamento } = useLicencaFaturamentoService();
      downloadFaturamento(state.filter)
        .then(() => {
          state.stateLoading = false;
        })
        .catch(() => {
          state.stateLoading = false;
        });
    };

    const submitSearch = () => {
      state.page = 0;
      state.tipoPaginamento = "faturadas";
      state.descricaoBusca = "Licenças Faturadas";
      return executeSearch();
    };

    const executeSave = (item: LicencaFaturamentoFilterPayload) => {
      state.stateLoading = true;
      const { salvarStatusItem } = useLicencaFaturamentoService();
      salvarStatusItem(item)
        .then(() => {
          state.stateLoading = false;
          useToast().success("AET alterada com sucesso");
        })
        .catch(() => {
          state.stateLoading = false;
          useToast().error("Falha ao salvar a AET");
        });
    };

    const submitSave = (data: LicencaFaturamentoFilterPayload) => {
      return executeSave(data);
    };

    const aetsToFaturar = () => {
      state.page = 0;
      state.tipoPaginamento = "afaturar";
      state.descricaoBusca = "Licenças a Faturar";
      return executeAetsToFaturar();
    };

    const gerarFaturamento = () => {
      return null;
    };

    const downloadPdf = () => {
      return executeDownload();
    };

    const getIdLicenca = (e?: LicencaFaturamentoPayload) => {
      return useLicencaFaturamentoService().getIdLicenca(e);
    };

    const getFlTipoItem = (e?: LicencaFaturamentoPayload) => {
      return useLicencaFaturamentoService().getFlTipoItem(e);
    };

    const getStatusFatura = (e?: LicencaFaturamentoPayload) => {
      return useLicencaFaturamentoService().getStatusFatura(e);
    };

    const getNomeTransportador = (e?: LicencaFaturamentoPayload) => {
      return useLicencaFaturamentoService().getNomeTransportador(e);
    };

    const getCpfCnpj = (e?: LicencaFaturamentoPayload) => {
      return useLicencaFaturamentoService().getCpfCnpj(e);
    };

    const getNumeroPedidoOrgao = (e?: LicencaFaturamentoPayload) => {
      return useLicencaFaturamentoService().getNumeroPedidoOrgao(e);
    };

    const blockedSelectionLicencaFaturamento = (
      itemFaturamento: LicencaFaturamentoPayload
    ) => {
      return !itemFaturamento?.idLicencaOrgao;
    };

    const afterPageChange = (page?: number) => {
      if (typeof page !== "undefined") {
        state.page = page;
      }
      if (state.tipoPaginamento == "afaturar") {
        executeAetsToFaturar();
      } else {
        executeSearch();
      }
    };

    const confirmSelectionFaturamento = (
      selected: Array<LicencaFaturamentoPayload>,
      added: Array<LicencaFaturamentoPayload>,
      removed: Array<LicencaFaturamentoPayload>
    ) => {
      if (added && added.length > 0) {
        added.forEach((lic) => {
          if (
            lic.idLicencaOrgao &&
            state.licencaOrgaoSelected.indexOf(lic.idLicencaOrgao) < 0
          ) {
            state.licencaOrgaoSelected.push(lic.idLicencaOrgao);
          }
        });
      }
      if (removed && removed.length > 0) {
        removed.forEach((lic) => {
          if (
            lic.idLicencaOrgao &&
            state.licencaOrgaoSelected.indexOf(lic.idLicencaOrgao) >= 0
          ) {
            state.licencaOrgaoSelected.splice(
              state.licencaOrgaoSelected.indexOf(lic.idLicencaOrgao),
              1
            );
          }
        });
      }
      return selected;
    };

    const confirmSelectionCliente = (c?: ClientePayload) => {
      state.filter.idCliente = c && c.idCliente ? c.idCliente : undefined;
      state.cliente = c;
      state.filter = { idCliente: state.filter.idCliente, statusFatura: "TOD" };
    };

    const confirmSelectionTransportador = (t?: TransportadorPayload) => {
      state.filter.idTransportador =
        t && t.idTransportador ? t.idTransportador : undefined;
    };

    onMounted(async () => {
      state.stateReady = true;
    });

    return {
      getIdLicenca,
      getFlTipoItem,
      getStatusFatura,
      getNomeTransportador,
      getCpfCnpj,
      getNumeroPedidoOrgao,
      submitSearch,
      //submitSave,
      aetsToFaturar,
      gerarFaturamento,
      downloadPdf,
      blockedSelectionLicencaFaturamento,
      afterPageChange,
      confirmSelectionFaturamento,
      amIAllowed,
      confirmSelectionCliente,
      confirmSelectionTransportador,
      formatDate,
      submitSave,
      stateBlocked,
      ...toRefs(state)
    };
  },
  components: {
    EntitySelectorTransportador,
    EntitySelectorCliente
  }
});
